var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createWriteStream } from 'streamsaver';
import { useCallback, useRef } from 'react';
import { hasValue, toStr } from '@utils';
import { format } from 'date-fns';
import useAlert from '@notifications/Alerts/useAlerts';
const DEFAULT_PAGE_SIZE = 100;
/**
 * Sanitizes a file name by removing invalid characters and ensuring a valid name
 * @param fileName - The input file name to sanitize
 * @returns A sanitized file name safe for use
 */
const sanitizeFileName = (fileName) => {
    const defaultName = 'export' + format(new Date(), 'yyyy-MM-dd-HH-mm-ss');
    if (!fileName || typeof fileName !== 'string') {
        return defaultName;
    }
    let sanitized = fileName.replace(/\.[^/.]+$/, '');
    sanitized = sanitized.replace(/[<>:"/\\|?*\x00-\x1f]/g, '');
    sanitized = sanitized.trim().replace(/^\.+|\.+$/g, '');
    sanitized = sanitized.replace(/\s+/g, ' ');
    sanitized = sanitized.substring(0, 100);
    return sanitized || defaultName;
};
/**
 * @export
 * Custom hook for streaming data to a CSV file with progress tracking and cancellation support
 *
 * @example
 * ```tsx
 * const { exportData, abortExport } = useExportData();
 *
 * // Start export
 * <button onClick={() => exportData({
 *   data: async (page, pageSize) => ({
 *     items: await fetchData(page, pageSize),
 *     totalCount: await fetchTotal()
 *   }),
 *   total: async () => await fetchTotal(),
 *   columns: [
 *     { id: 'name', label: 'Name' },
 *     { id: 'date', label: 'Date', formatter: (value) => format(new Date(value), 'yyyy-MM-dd') }
 *   ],
 *   pageSize: 100,
 *   fileName: 'data_export',
 *   showNamePrompt: true,
 *   onProgress: (progress) => console.log(`Progress: ${progress.progress}%`)
 * })}>Export Data</button>
 *
 * // Cancel export
 * <button onClick={abortExport}>Cancel Export</button>
 * ```
 */
export const useExportData = () => {
    const alerts = useAlert();
    const exportProgress = useRef({
        isExporting: false,
        progress: 0,
        currentPage: 1,
        totalRows: 0,
        processedRows: 0
    });
    const abortController = useRef(null);
    const updateProgress = useCallback((update, onProgress) => {
        exportProgress.current = Object.assign(Object.assign({}, exportProgress.current), update);
        onProgress === null || onProgress === void 0 ? void 0 : onProgress(exportProgress.current);
    }, []);
    const fetchTotal = useCallback((total, query, filters) => __awaiter(void 0, void 0, void 0, function* () {
        if (typeof total === 'number') {
            return total;
        }
        if (typeof total === 'function') {
            return yield total(query, filters);
        }
        return 0;
    }), []);
    const fetchDataPage = useCallback((data, page, currentPageSize, signal, query, filters, sort) => __awaiter(void 0, void 0, void 0, function* () {
        if (signal.aborted) {
            throw new DOMException('Export aborted', 'AbortError');
        }
        if (Array.isArray(data)) {
            const startIndex = (page - 1) * currentPageSize;
            const endIndex = startIndex + currentPageSize;
            return {
                items: data.slice(startIndex, endIndex),
                totalCount: data.length
            };
        }
        return yield data(page, currentPageSize, query, filters, sort);
    }), []);
    const getFileName = useCallback((providedFileName, showPrompt) => __awaiter(void 0, void 0, void 0, function* () {
        const shouldPrompt = !hasValue(providedFileName) || (showPrompt || false);
        if (shouldPrompt) {
            try {
                const formData = yield alerts.form({
                    title: 'Enter file name',
                    formStructure: [
                        {
                            title: '',
                            columns: 1,
                            fields: [
                                {
                                    name: 'name',
                                    label: 'File Name',
                                    type: 'text',
                                    validate: { required: true }
                                }
                            ]
                        }
                    ]
                });
                return (formData === null || formData === void 0 ? void 0 : formData.name) ? sanitizeFileName(formData.name) : null;
            }
            catch (_a) {
                return null;
            }
        }
        return sanitizeFileName(providedFileName);
    }), [alerts]);
    const formatValue = useCallback((value, row, column) => {
        if (column.formatter)
            return column.formatter(value, row);
        if (!hasValue(value))
            return '';
        return toStr(value);
    }, []);
    const exportData = useCallback((config) => __awaiter(void 0, void 0, void 0, function* () {
        const { data, total, columns, pageSize = DEFAULT_PAGE_SIZE, filters, sort, query, onProgress, fileName = '', showNamePrompt } = config;
        const controller = new AbortController();
        abortController.current = controller;
        try {
            const _fileName = yield getFileName(fileName, showNamePrompt);
            if (!_fileName) {
                return;
            }
            const totalRows = yield fetchTotal(total, query, filters);
            updateProgress({ isExporting: true, progress: 0, currentPage: 1, totalRows, processedRows: 0 }, onProgress);
            const fileStream = createWriteStream(_fileName + '.csv');
            const writer = fileStream.getWriter();
            const encoder = new TextEncoder();
            const headers = columns.map(col => col.label || col.id);
            const headerLine = headers.map(header => `"${header.replace(/"/g, '""')}"`).join(',');
            yield writer.write(encoder.encode('﻿' + headerLine + '\n'));
            while (exportProgress.current.isExporting && !controller.signal.aborted) {
                const result = yield fetchDataPage(data, exportProgress.current.currentPage, pageSize, controller.signal, query, filters, sort);
                if (!result.items || result.items.length === 0) {
                    updateProgress({ isExporting: false }, onProgress);
                    break;
                }
                const lines = result.items.map(row => {
                    const values = columns.map(col => {
                        const value = formatValue(row[col.id], row, col);
                        return `"${String(value).replace(/"/g, '""')}"`;
                    });
                    return values.join(',');
                });
                const chunk = lines.join('\n') + '\n';
                yield writer.write(encoder.encode(chunk));
                const processedRows = exportProgress.current.processedRows + result.items.length;
                const progress = totalRows > 0 ? Math.min((processedRows / totalRows) * 100, 100) : 100;
                updateProgress({
                    progress,
                    currentPage: exportProgress.current.currentPage + 1,
                    totalRows,
                    processedRows
                }, onProgress);
            }
            yield writer.close();
            updateProgress({ isExporting: false, progress: 100 }, onProgress);
        }
        catch (error) {
            if ((error === null || error === void 0 ? void 0 : error.name) === 'AbortError') {
                alerts.info('Export cancelled');
            }
            else {
                alerts.error('Export failed. Please try again.');
            }
            updateProgress({ isExporting: false, progress: 0, currentPage: 1, totalRows: 0, processedRows: 0 }, onProgress);
        }
        finally {
            abortController.current = null;
        }
    }), [getFileName, fetchTotal, fetchDataPage, formatValue, alerts, updateProgress]);
    /**
     * Cancels the ongoing export process
     */
    const abortExport = useCallback(() => {
        if (abortController.current) {
            abortController.current.abort();
            abortController.current = null;
        }
    }, []);
    return { exportData, abortExport };
};
